import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { Col, Row, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "motion/react";
import {
  faInstagram,
  faSpotify,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { getUserInvites } from "shared/service/StrmService";
import { moneyFormatObject, signByCurrency } from "shared/helpers/BRLFormat";
import { isMobile } from "react-device-detect";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { HomeButtonClickEventName } from "event-tracker/button-click-event-name";
import { notifyFirstAccess, UserState } from "explorer/reducers/users";
import {
  ArtistState,
  getUserArtistMilestones,
} from "explorer/reducers/artistas";
import { MilestoneRatioPeriod } from "shared/enums/MilestoneRatioPeriod";
import { ReactComponent as AdvanceCTAIcon } from "shared/assets/images/icon-advance-cta-home.svg";
import { ReactComponent as AchievementsIcon } from "shared/assets/images/icon-achievements.svg";
import { ReactComponent as LevelMenu } from "shared/assets/images/icon-level.svg";
import { ReactComponent as PromotionIcon } from "shared/assets/images/icon-promotion.svg";
import { ReactComponent as IconMetas } from "shared/assets/images/icon-metas.svg";
import Meta from "../metas/Meta";
import QuickQuincy from "shared/components/quick-quincy";
import CareerLevelBadge from "./CareerLevelBadge";
import "./Home.scss";
import { DistributionStatusEnum } from "shared/enums/DistributionStatusEnum";
import { useFlags } from "launchdarkly-react-client-sdk";
import HomeSkeleton from "./HomeSkeleton";
import {
  AdvanceState,
  canAskAdvance,
  getLoggedUserCashAdvanceAvailable,
} from "explorer/reducers/adiantamentos";
import { CashAdvanceAvailableEnum } from "shared/enums/CashAdvanceAvailable";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";
import ModalSignatureComplete from "./ModalSignature";
import ModalBreakingSound from "./modal-breaking-sound/ModalBreakingSound";
import ModalSubscriptionRelease from "../new-estagio-artista/ModalSubscriptionRelease";

enum Tab {
  Spotify = "spotify",
  Youtube = "youtube",
  Instagram = "instagram",
  Achievements = "achievements",
}

interface CareerProgressCircleProps {
  width?: number;
  height?: number;
  fontSizeLabel?: number;
  fontSizePercent?: number;
  scaleFactor?: number;
}

const CareerProgressCircle: React.FC<CareerProgressCircleProps> = ({
  width = 100,
  height = 100,
  fontSizeLabel = 10,
  fontSizePercent = 14,
  scaleFactor = 0.6,
}) => {
  const { t } = useTranslation();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const radius = Math.min(width, height) / 2 - 10;
  const circumference = 2 * Math.PI * radius;
  const progress = userArtist.stagePercentual || 0;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="progress-wrapper" style={{ width, height }}>
      <svg width={width} height={height} className="progress-ring">
        <circle
          r={radius}
          cx={width / 2}
          cy={height / 2}
          fill="transparent"
          stroke="#d2bfff"
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>

      <div
        className="background-image"
        style={{
          backgroundImage: `url(${userArtist.profileImage})`,
          width: width * scaleFactor,
          height: height * scaleFactor,
          top: (height - height * scaleFactor) / 2,
          left: (width - width * scaleFactor) / 2,
        }}
      >
        <div className="progress-text">
          <div>
            <span style={{ fontSize: fontSizePercent }}>
              {progress.toFixed(0)}%
            </span>
          </div>
          <div style={{ fontSize: fontSizeLabel, color: "#CECDFF" }}>
            {t("pages.Home.toLevel")}
          </div>
        </div>
      </div>
    </div>
  );
};

const Home: React.FC<RouteComponentProps> = (props) => {
  const { history, location } = props;

  const [loadingInvite, setLoadingInvite] = useState<boolean>(false);
  const [invites, setInvites] = useState<any[]>([]);
  const [checkedInvites, setCheckedInvites] = useState(false);
  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.Spotify);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    loggedUser,
    loading,
    firstAccess,
    firstAccessNotified,
    shouldOpenTour,
  } = useSelector((state: UserState) => state.users);
  const {
    userArtist,
    loading: loadingArtist,
    milestones,
  } = useSelector((state: ArtistState) => state.artists);

  const { cashAdvanceAvailable } = useSelector(
    (state: AdvanceState) => state.advances
  );
  const { showHome, enableTour } = useFlags();
  const level = userArtist.stage || 0;
  let { titulo } = t(`pages.EstagioArtista.estagio.${level || 0}`) as any;

  const noData =
    !milestones?.achievements &&
    !milestones?.instagram &&
    !milestones?.spotify &&
    !milestones?.youtube;

  const permiteAssinarDistroStrm = () =>
    false && // ocultado a pedido do time de produto em 16/09/2025
    userArtist?.distribution?.available &&
    userArtist?.distribution?.status === DistributionStatusEnum.NotSigned;

  const getPropBySelectedTab = () => {
    if (!milestones) return [];
    return milestones[selectedTab];
  };

  const mensagemSemAdiantamento = () => {
    if (
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotAvailable ||
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound
    )
      return t("pages.Adiantamentos.naoCumpreRequisitosMensagemSimples", {
        currency: signByCurrency(userArtist?.cashAdvanceEstimated?.currency),
      });

    if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NeedRetry)
      return t("pages.Adiantamentos.tentarNovamenteEmbreve");

    // if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound)
    //   return t("pages.Adiantamentos.spotifyNaoEncontrado");
  };

  const onClickTab = (tab: Tab) => {
    setSelectedTab(tab);
    dataLayerClickedEvent(HomeButtonClickEventName.GOALS_INTERACTION, {
      ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
      businessContext: "HOME_V2",
    });
  };

  const onClickViewMoreAdvance = () => {
    history.push("/adiantamentos");
    dataLayerClickedEvent(HomeButtonClickEventName.ADVANCE_VIEW_MORE, {
      ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
      businessContext: "HOME_V2",
    });
  };

  const onClickBoostMeta = (meta: any) => {
    if (meta.name === "Followers_cnt_spotify") {
      dataLayerClickedEvent(HomeButtonClickEventName.BOOST_SPOTIFY_FOLLOWERS, {
        ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
        businessContext: "HOME_V2",
      });
    }
    if (meta.name === "Popularity_spotify") {
      dataLayerClickedEvent(HomeButtonClickEventName.BOOST_SPOTIFY_POPULARITY, {
        ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
        businessContext: "HOME_V2",
      });
    }
    history.push("/promotion");
  };

  useEffect(() => {
    const fetchInvites = async () => {
      setLoadingInvite(true);
      const response: any = await getUserInvites();
      setInvites(response || []);
      setCheckedInvites(true);
      setLoadingInvite(false);
    };
    if (!loading && !loadingInvite && !checkedInvites) fetchInvites();
  }, [loading, loadingInvite, checkedInvites]);

  useEffect(() => {
    if (!checkedInvites) return;
    if (invites.length > 0) {
      history.push("/convites");
    } else if (!showHome && (userArtist?.stage ?? 0) > 0) {
      history.push("/estagio");
    }
  }, [checkedInvites, invites, userArtist?.stage, history, showHome]);

  useEffect(() => {
    if (!loading && !userArtist?.stage) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Home.S4A_HOME_UNDIAGNOSED,
      });
    }
  }, [loading, userArtist]);

  useEffect(() => {
    if (userArtist.id) {
      dispatch(
        getUserArtistMilestones({
          artistId: userArtist.id,
          period: MilestoneRatioPeriod.TwelveMonths,
        })
      );
    }
  }, [userArtist, dispatch]);

  useEffect(() => {
    if (
      !loading &&
      !loadingArtist &&
      (userArtist?.stage || 0) > 0 &&
      !firstAccessNotified
    ) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
        businessContext: "HOME_V2",
        carreerStage: userArtist?.stage,
        approvedLimit: userArtist?.cashAdvanceEstimated?.amountUsd,
        alreadyCalculated:
          userArtist?.cashAdvanceEstimated?.successfullyEstimated,
        country: userArtist?.country,
        firstAccess: firstAccess,
        activeAdvanceContract: userArtist?.cashAdvanceSigned,
        activeDistributionContract:
          userArtist?.distribution.status === DistributionStatusEnum.Signed,
        registerDate: loggedUser?.firstLoginArtistsAt,
      });

      dispatch(notifyFirstAccess());
    }
  }, [
    loading,
    firstAccessNotified,
    loggedUser,
    userArtist,
    dispatch,
    history,
    firstAccess,
    loadingArtist,
  ]);

  useEffect(() => {
    if (enableTour && shouldOpenTour) {
      history.push({
        pathname: "/tour",
        search: location.search || "",
      });
    }
  }, [enableTour, history, shouldOpenTour, location.search]);

  useEffect(() => {
    try {
      if (location?.pathname === "/") {
        history.replace("/home");
      }
    } catch (e) {}
  }, [history, location]);

  useEffect(() => {
    dispatch(getLoggedUserCashAdvanceAvailable(false));
  }, [dispatch]);

  // if (loading || loadingArtist) {
  //   if (!showHome && userArtist?.stage) {
  //     return <Loader.Simple />;
  //   }
  //   return <HomeSkeleton />;
  // }

  if (loading || loadingArtist || (!showHome && userArtist?.stage)) {
    return <HomeSkeleton />;
  }

  if (level > 0 && showHome) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className={`home-page ${isMobile ? "mobile" : ""}`}
      >
        <ModalSignatureComplete />
        <ModalBreakingSound />
        <ModalSubscriptionRelease />
        <Row justify="center">
          <Col xs={24} sm={12} style={{ maxWidth: isMobile ? "auto" : 900 }}>
            <div className="home-container">
              {isMobile && (
                <div
                  className="bg-gradient"
                  style={{
                    height: 80,
                  }}
                />
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 1,
                  marginBottom: 12,
                }}
              >
                <SubscriptionDefaultCTA />
              </div>
              <div
                className="artist-level-banner"
                style={{
                  gap: isMobile ? 12 : 20,
                }}
              >
                <div
                  className="level-indicator"
                  onClick={() => history.push("/estagio")}
                  style={{ cursor: "pointer" }}
                >
                  <CareerLevelBadge
                    level={level}
                    width={isMobile ? 60 : 86}
                    height={isMobile ? 60 : 92}
                  />
                </div>
                <div className="level-info">
                  <h5
                    className="heading-h5"
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push("/estagio")}
                  >
                    {titulo}
                  </h5>
                  <p className="body-regular">{t("pages.Home.subtitle")}</p>
                </div>
                <CareerProgressCircle
                  width={isMobile ? 90 : 100}
                  height={isMobile ? 90 : 100}
                  fontSizeLabel={10}
                  fontSizePercent={14}
                  scaleFactor={0.7}
                />
              </div>
              <div
                style={{
                  marginTop: isMobile ? 20 : 40,
                  height: "1px",
                  backgroundColor: "#1B1A4C",
                }}
              />
              <div className="quincy-step">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <span className="title">{t("pages.Home.nextMoves")}</span>
                  <Link
                    to="/quincy"
                    className="green-label"
                    onClick={() => {
                      dataLayerClickedEvent(
                        HomeButtonClickEventName.QUINCY_VIEW_MORE,
                        {
                          ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
                          businessContext: "HOME_V2",
                        }
                      );
                    }}
                  >
                    {t("pages.Home.viewMore")}
                  </Link>
                </div>
                <QuickQuincy.Home />
              </div>
              <div className="advance-card">
                <div className="advance-content">
                  <div className="advance-section">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        className="title"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <AdvanceCTAIcon stroke="#CECDFF" />
                        {t("pages.Home.advance")}
                      </span>
                      <Link
                        to="/adiantamentos"
                        className="green-label"
                        onClick={() => {
                          dataLayerClickedEvent(
                            HomeButtonClickEventName.ADVANCE_VIEW_MORE,
                            {
                              ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
                              businessContext: "HOME_V2",
                            }
                          );
                        }}
                      >
                        {t("pages.Home.viewMore")}
                      </Link>
                    </div>
                    <div className="advance-card-gradient-border">
                      <div className="advance-card">
                        <div
                          className={
                            isMobile
                              ? "advance-background-mobile"
                              : "advance-background"
                          }
                          // style={{
                          //   backgroundSize: isMobile ? "cover" : "100% 100%",
                          //   backgroundPosition: isMobile ? "right" : "center",
                          // }}
                        />

                        <div className="advance-content">
                          {canAskAdvance(cashAdvanceAvailable) ||
                          loggedUser?.isCashAdvanceAvailable ? (
                            <>
                              <div
                                className="advance-amount"
                                style={{ marginTop: 24 }}
                              >
                                <span
                                  className={`amount-text ${
                                    isMobile ? "body-reguler" : "heading-h5"
                                  }`}
                                >
                                  {moneyFormatObject(
                                    userArtist?.cashAdvanceEstimated
                                  )}{" "}
                                  {t("pages.Home.available")}
                                </span>
                              </div>
                              <p
                                className={`advance-description ${
                                  isMobile ? "body-small" : "body-regular"
                                }`}
                              >
                                {t("pages.Home.advanceCTA")}
                              </p>
                              <button
                                className="botao-branco"
                                style={{
                                  marginTop: 16,
                                  fontSize: 14,
                                }}
                                onClick={onClickViewMoreAdvance}
                              >
                                {t("pages.Home.requestAdvance")}
                              </button>
                            </>
                          ) : (
                            <>
                              <p className="branco">
                                {t(
                                  "pages.EstagioArtista.adiantamentoNaoLiberadoTitulo"
                                )}
                              </p>
                              <p
                                className={`advance-description ${
                                  isMobile ? "body-small" : "body-regular"
                                }`}
                              >
                                <Trans i18nKey={mensagemSemAdiantamento()} />
                              </p>
                            </>
                          )}
                        </div>

                        <div className="advance-gradient-line"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="goals">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <span
                    className="title"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <IconMetas className="icon" stroke="#bcb8f7" />
                    {t("pages.Home.goals")}
                  </span>
                  <Link
                    to="/metas"
                    className="green-label"
                    onClick={() => {
                      dataLayerClickedEvent(
                        HomeButtonClickEventName.GOALS_VIEW_MORE,
                        {
                          ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
                          businessContext: "HOME_V2",
                        }
                      );
                    }}
                  >
                    {t("pages.Home.viewMore")}
                  </Link>
                </div>
                <div>
                  <div className="scrollmenu" hidden={noData}>
                    <div className="scroll-box">
                      <Tooltip title="Spotify">
                        <button
                          className={`body-regular ${
                            selectedTab === "spotify" ? "selected" : ""
                          }`}
                          onClick={() => {
                            onClickTab(Tab.Spotify);
                          }}
                        >
                          <FontAwesomeIcon icon={faSpotify} color="#56567C" />
                          Spotify
                        </button>
                      </Tooltip>
                    </div>
                    <div className="scroll-box">
                      <Tooltip title="Youtube">
                        <button
                          className={`body-regular ${
                            selectedTab === "youtube" ? "selected" : ""
                          }`}
                          onClick={() => onClickTab(Tab.Youtube)}
                        >
                          <FontAwesomeIcon icon={faYoutube} color="#56567C" />
                          YouTube
                        </button>
                      </Tooltip>
                    </div>
                    <div className="scroll-box">
                      <Tooltip title="Instagram">
                        <button
                          className={`body-regular ${
                            selectedTab === "instagram" ? "selected" : ""
                          }`}
                          onClick={() => onClickTab(Tab.Instagram)}
                        >
                          <FontAwesomeIcon icon={faInstagram} color="#56567C" />
                          Instagram
                        </button>
                      </Tooltip>
                    </div>
                    <div className="scroll-box">
                      <Tooltip title={t(`pages.Metas.conquistas`)}>
                        <button
                          className={`body-regular ${
                            selectedTab === "achievements" ? "selected" : ""
                          }`}
                          onClick={() => onClickTab(Tab.Achievements)}
                        >
                          <AchievementsIcon stroke="#56567C" />
                          {t(`pages.Metas.conquistas`)}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    className="meta-container"
                    style={{ minHeight: loading ? 200 : 0 }}
                  >
                    {getPropBySelectedTab()?.map((meta, idx) => (
                      <Meta
                        {...meta}
                        key={meta.name}
                        animationDelay={idx * 0.1}
                        footer={
                          selectedTab === Tab.Spotify && (
                            <div
                              className="meta-footer body-small"
                              onClick={() => onClickBoostMeta(meta)}
                            >
                              <PromotionIcon stroke="#F680C8" width={16} />
                              <span>
                                <b
                                  className="bold-text"
                                  style={{ display: "block" }}
                                >
                                  {t("pages.Home.promoteYourSongTitle")}
                                </b>
                                {t("pages.Home.promoteYourSongText")}
                              </span>
                            </div>
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
              {permiteAssinarDistroStrm() && (
                <div className="cta-distro-card">
                  <div className="background" />
                  <div className="content">
                    <span
                      className="body-small"
                      style={{
                        color: "#cecdff",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <LevelMenu width={21} stroke="#bcb8f7" />
                      {t("pages.Home.distroCTATitle")?.toLocaleUpperCase()}
                    </span>
                    <h3 className="heading-h4">
                      {t("pages.Home.distroCTASubtitle")}
                    </h3>
                    <button
                      className="botao-gradiente-rosa-roxo"
                      style={{ width: "100%" }}
                      onClick={() => history.push("/distribua")}
                    >
                      {t("pages.Home.distroCTA")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </motion.div>
    );
  }

  return (
    <div className="home">
      <div className="direction">
        <div className="content">
          <h1>{t("pages.Home.welcome")}</h1>
          <h3>{t("pages.Home.subtext")}</h3>
          <span className="message">{t("pages.Home.intro")}</span>
          <button
            className="botao-roxo"
            onClick={() => {
              dataLayerClickedEvent(HomeButtonClickEventName.RUN_DIAGNOSE, {
                ...tagManagerEvent.Home.S4A_HOME_UNDIAGNOSED,
              });

              history.push("/quiz");
            }}
          >
            {t("pages.Home.start")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
